import { useAppSelector } from "../../app";
import { BlockingEntity } from "../../configMap/interfaces/entity";
import { useMultipleGroupBlocks } from "../../configMap";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  changeActiveButton,
  changeActiveEntity,
  changeAnnotating,
} from "../../app/store/appSlice";
import { setIncompleteEntities } from "../store/metadataSlice";
import selectAnnotationReducer from "../../annotation/selectors/annotationReducerSelector";

const useValidateBlockingEntities = () => {
  const dispatch = useDispatch();

  const groupBlocks = useAppSelector(
    (state) => state.configMapReducer.configMap?.groupBlocks,
  );

  const annotations = useAppSelector(selectAnnotationReducer);

  const enforceRequiredEntities = useAppSelector(
    (state) =>
      state.documentReducer.activeDocument?.documentType
        ?.enforceRequiredEntities,
  );

  const { countPerGroupBlockId } = useMultipleGroupBlocks();

  const getBlockingEntities = useCallback(() => {
    if (!groupBlocks || !enforceRequiredEntities) {
      return null;
    }

    return groupBlocks.flatMap((groupBlock) => {
      const requiredEntityTypes = groupBlock.groupBlockEntityTypes.filter(
        (gbet) => gbet.required,
      );

      if (!requiredEntityTypes.length) {
        return [];
      }

      const groupBlockItemCount = groupBlock.multipleGroupBlocks
        ? countPerGroupBlockId(groupBlock.id)
        : 1;

      return requiredEntityTypes.flatMap((gbet) =>
        Array.from({ length: groupBlockItemCount }, (_, index) => ({
          entityTypeId: gbet.entityType.id,
          groupBlockId: groupBlock.multipleGroupBlocks
            ? groupBlock.id
            : undefined,
          index: index + 1,
        })),
      );
    });
  }, [groupBlocks, countPerGroupBlockId, enforceRequiredEntities]);

  const setIncompleteEntitiesAndScrollTo = useCallback(
    (incompleteEntities: Array<BlockingEntity>) => {
      dispatch(setIncompleteEntities(incompleteEntities));

      if (!incompleteEntities.length) {
        return;
      }

      const { entityTypeId, groupBlockId, index } = incompleteEntities[0];
      const groupBlockSuffix = groupBlockId ? `-${groupBlockId}` : "";
      const activeField = `entity-${entityTypeId}${groupBlockSuffix}-${index}`;

      dispatch(changeAnnotating(false));
      dispatch(changeActiveEntity(undefined));
      dispatch(changeActiveButton({ id: activeField }));
    },
    [dispatch],
  );

  return useCallback(() => {
    const blockingEntities = getBlockingEntities();
    if (!blockingEntities) {
      return true;
    }

    const incompleteEntities: Array<BlockingEntity> = blockingEntities.filter(
      (blocker) =>
        !annotations.some(
          (annotation) =>
            annotation.entity?.id === blocker.entityTypeId &&
            annotation.index === blocker.index &&
            !annotation.tempAnnotation,
        ),
    );

    setIncompleteEntitiesAndScrollTo(incompleteEntities);

    return !incompleteEntities.length;
  }, [getBlockingEntities, annotations, setIncompleteEntitiesAndScrollTo]);
};

export default useValidateBlockingEntities;
